import {
  PREFERENCES_TEXT_KEY,
  PREFERENCES_TEXT_KEY_LEGACY,
  YODL_ENS_DOMAIN,
} from '@frontend/common/constants';
import {
  convertPreferencesToText,
  PreferencesSchema,
} from '@frontend/shared/validation/preferences.schema';
import {
  useAddSubname,
  usePrimaryName,
  useRecords,
  useUpdateSubname,
} from '@justaname.id/react';
import { useMutation } from '@tanstack/react-query';
import assert from 'assert';
import { useMemo } from 'react';
import { isAddress } from 'viem';
import { mainnet } from 'viem/chains';

/**
 * Hook to retrieve signed preferences for an address or ENS name
 */
const useSignedPreferences = ({ addressOrEns }: { addressOrEns?: string }) => {
  const isAddressValue = isAddress(addressOrEns || '');

  // If address get the primary name
  const { primaryName, isPrimaryNameLoading, isPrimaryNameFetching } =
    usePrimaryName({
      address: isAddressValue ? addressOrEns : undefined,
      enabled: !!isAddressValue,
      chainId: mainnet.id,
      priority: 'offChain',
    });

  const ensName = useMemo(() => {
    if (isAddressValue) return primaryName;
    return addressOrEns;
  }, [isAddressValue, primaryName, addressOrEns]);

  const { records, isRecordsLoading, isRecordsFetching, isRecordsPending } =
    useRecords({
      ens: ensName,
      chainId: mainnet.id,
    });

  const preferences = useMemo(() => {
    const findRecord = (key: string) => {
      return records?.records.texts.find((record) => record.key.trim() === key);
    };

    // Parse preferences, trying current format first, then legacy
    const result = findRecord(PREFERENCES_TEXT_KEY);
    const resultLegacy = findRecord(PREFERENCES_TEXT_KEY_LEGACY);

    const preferencesText = result?.value || resultLegacy?.value;

    if (!preferencesText) return undefined;

    try {
      const parseResult = PreferencesSchema.safeParse(preferencesText);

      if (!parseResult.success) {
        console.error('Preferences validation failed:', parseResult.error);
        return undefined;
      }

      return parseResult.data;
    } catch (error) {
      console.error('Failed to parse preferences:', error);
      return undefined;
    }
  }, [records]);

  return {
    preferences,
    isLoading: isPrimaryNameLoading || (isRecordsLoading && !!ensName),
    isFetching: isRecordsFetching || isPrimaryNameFetching,
    isPending: isRecordsPending,
  };
};

interface Preferences {
  chains: number[];
  tokens: string[];
  [key: string]: any; // Allow for additional properties
}

/**
 * Hook to sign and save preferences
 */
const useSignPreferences = ({
  username,
  isRegistered,
  onError,
  onSuccess,
}: {
  username?: string;
  isRegistered?: boolean;
  onError?: (error: any) => void;
  onSuccess?: () => void;
}) => {
  const { addSubname } = useAddSubname();
  const { updateSubname } = useUpdateSubname();

  return useMutation({
    mutationFn: async ({ tokens, chains, ...rest }: Preferences) => {
      assert(username, 'ENS is required');

      const text = {
        [PREFERENCES_TEXT_KEY]: convertPreferencesToText({
          tokens,
          chains,
          ...rest,
        }),
      };

      // Update or add subname based on registration status
      if (isRegistered) {
        await updateSubname({
          text,
          ens: username,
        });
      } else {
        await addSubname({
          text,
          username,
          ensDomain: YODL_ENS_DOMAIN,
        });
      }
    },
    onError: (error, variables, context) => {
      console.error('Error signing preferences:', error);
      console.log('variables', variables);
      console.log('context', context);
      console.log('username', username);
      onError?.(error);
    },
    onSuccess: () => {
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export { useSignPreferences, useSignedPreferences };
